import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import AppVersionService from "../../api/services/appVersionService";
import Loading from "../../components/Loading";
import SectionCard from "../../components/common/SectionCard";
import Modal from "../../components/common/Modal";
import VersionForm from "../../components/app-version/VersionForm";
import VersionTable from "../../components/app-version/VersionTable";

const AppVersion = () => {
  const navigate = useNavigate();

  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
  });
  const [form, setForm] = useState({
    platform: "",
    version: "",
  });

  const resetForm = () => {
    setEditingVersion(null);
    setForm({
      platform: "",
      version: "",
    });
  };

  const fetchVersions = async () => {
    try {
      setIsLoading(true);
      const response = await AppVersionService.getAllVersions();
      setVersions(response?.data || []);
    } catch (err) {
      if (err?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error("Failed to load version list");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const openCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingVersion(item?.id);
    setForm({
      platform: item?.platform,
      version: item?.version,
    });
    setModalOpen(true);
  };

  const saveVersion = async () => {
    const payload = {
      ...form,
      forceUpdate: Number(form.forceUpdate),
      isActive: Number(form.isActive),
    };

    try {
      const action = editingVersion
        ? AppVersionService.updateVersion(editingVersion, payload)
        : AppVersionService.createVersion(payload);

      await toast.promise(action, {
        loading: editingVersion ? "Updating..." : "Creating...",
        success: editingVersion ? "Version updated" : "Version added",
        error: "Failed to save version",
      });

      setModalOpen(false);
      resetForm();
      fetchVersions();
    } catch (err) {
      if (err?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
      toast.error("Something went wrong");
    }
  };

  const performDelete = async (id) => {
    if (!confirmDelete.open) return;
    try {
      await toast.promise(AppVersionService.deleteVersion(id), {
        loading: "Deleting...",
        success: "Deleted successfully",
        error: "Failed to delete",
      });
      await fetchVersions();
    } catch (err) {
      if (err?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
        return;
      }
    }
    setConfirmDelete({ open: false, id: null });
  };

  const requestDeleteCategory = (id) => {
    setConfirmDelete({ open: true, id });
  };

  return (
    <div className="w-full p-4 overflow-x-hidden">
      <SectionCard
        title="App Version Control"
        subtitle="Manage iOS / Android / Web versions"
        action={
          <button
            type="button"
            onClick={openCreate}
            className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
            Add Version
          </button>
        }>
        <div className="overflow-x-auto">
          <VersionTable
            versions={versions}
            loading={isLoading}
            onEdit={openEdit}
            onDelete={requestDeleteCategory}
          />
        </div>
      </SectionCard>

      <Modal
        open={modalOpen}
        title={editingVersion ? "Edit Version" : "Add Version"}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-gray-300 bg-white px-3 py-2 text-gray-900">
              Cancel
            </button>
            <button
              type="button"
              onClick={saveVersion}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              {editingVersion ? "Save Changes" : "Create"}
            </button>
          </>
        }>
        <VersionForm form={form} setForm={setForm} />
      </Modal>

      <Modal
        open={confirmDelete.open}
        title="Confirm Delete"
        onClose={() => setConfirmDelete({ open: false, id: null })}
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirmDelete({ open: false, id: null })}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-gray-300 bg-white px-3 py-2 text-gray-900">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => performDelete(confirmDelete.id)}
              className="cursor-pointer rounded-lg text-xs md:text-sm border border-red-500 bg-red-500 px-4 py-2 text-white font-semibold hover:bg-red-600 transition-colors shadow-md">
              Delete
            </button>
          </>
        }>
        <div className="text-gray-700">
          Are you sure you want to delete this version? This action cannot be
          undone.
        </div>
      </Modal>
    </div>
  );
};

export default AppVersion;
